// ===============================================================
// 📜 WithdrawHistoryPage — LIVORA / EARNINGS1 / INSTANT-WITHDRAW READY
// path: src/pages/withdraw/WithdrawHistoryPage.js
// collection: withdrawals
// compatible with legacy statuses while presenting instant-withdraw UX
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { auth, db } from "../../firebase";
import TopBar2 from "../../components/common/TopBar2";
import "./WithdrawHistoryPage.css";
import { formatNumberEn, formatDateTimeEn } from "../../utils/digits";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

const FILTERS = ["all", "completed", "processing", "failed"];
const TOKENS_PER_USD = 100;

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatMoney(amountUsd) {
  return `$${toNumber(amountUsd).toFixed(2)}`;
}

function formatTokens(tokens) {
  return formatNumberEn(toNumber(tokens));
}

function getAmountUsd(item) {
  if (item?.amountUsd !== undefined && item?.amountUsd !== null) {
    return toNumber(item.amountUsd);
  }

  if (item?.amountTokens !== undefined && item?.amountTokens !== null) {
    return toNumber(item.amountTokens) / TOKENS_PER_USD;
  }

  return 0;
}

function getAmountTokens(item) {
  if (item?.amountTokens !== undefined && item?.amountTokens !== null) {
    return toNumber(item.amountTokens);
  }

  if (item?.amountUsd !== undefined && item?.amountUsd !== null) {
    return Math.floor(toNumber(item.amountUsd) * TOKENS_PER_USD);
  }

  return 0;
}

function normalizeStatus(status) {
  switch (status) {
    case "paid":
    case "completed":
    case "success":
      return "completed";

    case "pending":
    case "under_review":
    case "approved":
    case "approved_for_payout":
    case "ready_for_admin_review":
    case "processing":
      return "processing";

    case "rejected":
    case "cancelled":
    case "failed":
      return "failed";

    default:
      return "unknown";
  }
}

export default function WithdrawHistoryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: must never query or
    // subscribe to the withdrawals collection while real-money payouts
    // are disabled - historical cash-payout data (amounts, methods,
    // statuses) must not be read into the client at all, direct route
    // access included. Existing Firestore documents are left untouched
    // for future reactivation.
    if (!ENABLE_REAL_MONEY_PAYOUTS) {
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      navigate("/login");
      return;
    }

    const q = query(
      collection(db, "withdrawals"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setItems(list);
        setLoading(false);
        setErrorText("");
      },
      (err) => {
        console.error("WithdrawHistoryPage snapshot error:", err);
        setLoading(false);
        setErrorText(
          t("withdrawHistory.loadError", {
            defaultValue: "تعذر تحميل سجل السحب حاليًا.",
          })
        );
      }
    );

    return () => unsub();
  }, [navigate, t]);

  const filteredItems = useMemo(() => {
    if (selectedFilter === "all") return items;

    return items.filter((item) => normalizeStatus(item.status) === selectedFilter);
  }, [items, selectedFilter]);

  function getStatusLabel(status) {
    if (status === "pending") {
      return t("withdrawHistory.status.pending", {
        defaultValue: "تم إرسال الطلب",
      });
    }

    if (status === "under_review") {
      return t("withdrawHistory.status.under_review", {
        defaultValue: "قيد مراجعة الإدارة",
      });
    }

    if (status === "approved") {
      return t("withdrawHistory.status.approved", {
        defaultValue: "تمت الموافقة بانتظار الدفع",
      });
    }

    if (status === "approved_for_payout") {
      return t("withdrawHistory.status.approved_for_payout", {
        defaultValue: "مؤهلة للسحب",
      });
    }

    if (status === "ready_for_admin_review") {
      return t("withdrawHistory.status.ready_for_admin_review", {
        defaultValue: "قيد مراجعة أهلية السحب",
      });
    }

    if (status === "paid") {
      return t("withdrawHistory.status.paid", {
        defaultValue: "تم الدفع",
      });
    }

    if (status === "rejected") {
      return t("withdrawHistory.status.rejected", {
        defaultValue: "مرفوضة",
      });
    }

    if (status === "cancelled") {
      return t("withdrawHistory.status.cancelled", {
        defaultValue: "ملغاة",
      });
    }

    const normalized = normalizeStatus(status);

    switch (normalized) {
      case "completed":
        return t("withdrawHistory.status.completed", {
          defaultValue: "مكتملة",
        });

      case "processing":
        return t("withdrawHistory.status.processing", {
          defaultValue: "قيد التنفيذ",
        });

      case "failed":
        return t("withdrawHistory.status.failed", {
          defaultValue: "غير ناجحة",
        });

      default:
        return t("withdrawHistory.status.unknown", {
          defaultValue: "غير معروفة",
        });
    }
  }

  function getStatusClass(status) {
    const normalized = normalizeStatus(status);

    switch (normalized) {
      case "completed":
        return "is-paid";
      case "processing":
        return "is-review";
      case "failed":
        return "is-rejected";
      default:
        return "is-default";
    }
  }

  function getMethodLabel(method, payoutDetails) {
    if (method === "crypto") {
      const asset = payoutDetails?.asset || itemSafe(payoutDetails?.asset, "USDT");
      const network = payoutDetails?.network || "";
      return network ? `${asset} (${network})` : asset;
    }

    if (method === "paypal") {
      return t("withdrawHistory.method.paypal", {
        defaultValue: "PayPal",
      });
    }

    if (method === "bank") {
      return t("withdrawHistory.method.bank", {
        defaultValue: "تحويل بنكي",
      });
    }

    return (
      method ||
      t("common.dash", {
        defaultValue: "—",
      })
    );
  }

  function itemSafe(value, fallback = "") {
    return value ?? fallback;
  }

  function formatDate(createdAt) {
    try {
      if (!createdAt?.toDate)
        return t("common.dash", {
          defaultValue: "—",
        });
      return formatDateTimeEn(createdAt.toDate(), {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return t("common.dash", {
        defaultValue: "—",
      });
    }
  }

  // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: while real-money payouts are
  // disabled, render a neutral unavailable notice only - no USD amounts,
  // payout methods, or cash-payout statuses/history. The original list
  // rendering below (amounts, methods, statuses) is preserved unchanged
  // (not deleted) for reactivation later; historical Firestore documents
  // are never queried or displayed here while disabled.
  if (!ENABLE_REAL_MONEY_PAYOUTS) {
    return (
      <div className="withdraw-history-page">
        <TopBar2
          title={t("withdrawHistory.title", {
            defaultValue: "سجل السحب",
          })}
          onBack={() => navigate(-1)}
        />

        <div className="withdraw-history-page__spacer" />

        <div className="withdraw-history-page__content">
          <div className="withdraw-history-hero">
            <h1 className="withdraw-history-hero__title">
              {t("withdrawHistory.title", {
                defaultValue: "سجل السحب",
              })}
            </h1>

            <p className="withdraw-history-hero__subtitle">
              {t("withdraw.help.payoutsDisabledNotice", {
                defaultValue:
                  "Cash withdrawals are currently unavailable. Tokens are virtual and cannot be redeemed for cash.",
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdraw-history-page">
      <TopBar2
        title={t("withdrawHistory.title", {
          defaultValue: "سجل السحب",
        })}
        onBack={() => navigate(-1)}
      />

      <div className="withdraw-history-page__spacer" />

      <div className="withdraw-history-page__content">
        <div className="withdraw-history-hero">
          <h1 className="withdraw-history-hero__title">
            {t("withdrawHistory.title", {
              defaultValue: "سجل السحب",
            })}
          </h1>

          <p className="withdraw-history-hero__subtitle">
            {t("withdrawHistory.subtitle", {
              defaultValue: "راجعي عمليات السحب السابقة وحالتها الحالية بسهولة.",
            })}
          </p>
        </div>

        <div className="withdraw-history-filters">
          {FILTERS.map((filterKey) => (
            <button
              key={filterKey}
              type="button"
              className={`withdraw-history-filter ${
                selectedFilter === filterKey ? "is-active" : ""
              }`}
              onClick={() => setSelectedFilter(filterKey)}
            >
              {filterKey === "all" &&
                t("withdrawHistory.filters.all", { defaultValue: "الكل" })}
              {filterKey === "completed" &&
                t("withdrawHistory.filters.completed", {
                  defaultValue: "مكتملة",
                })}
              {filterKey === "processing" &&
                t("withdrawHistory.filters.processing", {
                  defaultValue: "قيد التنفيذ",
                })}
              {filterKey === "failed" &&
                t("withdrawHistory.filters.failed", {
                  defaultValue: "غير ناجحة",
                })}
            </button>
          ))}
        </div>

        {loading && (
          <div className="withdraw-history-state-card">
            {t("withdrawHistory.loading", {
              defaultValue: "جارٍ تحميل سجل السحب...",
            })}
          </div>
        )}

        {!loading && errorText && (
          <div className="withdraw-history-state-card is-error">
            {errorText}
          </div>
        )}

        {!loading && !errorText && filteredItems.length === 0 && (
          <div className="withdraw-history-state-card">
            {t("withdrawHistory.empty", {
              defaultValue: "لا توجد عمليات سحب حتى الآن.",
            })}
          </div>
        )}

        {!loading && !errorText && filteredItems.length > 0 && (
          <div className="withdraw-history-list">
            {filteredItems.map((item) => {
              const amountUsd = getAmountUsd(item);
              const amountTokens = getAmountTokens(item);

              return (
                <div key={item.id} className="withdraw-history-card">
                  <div className="withdraw-history-card__top">
                    <div className="withdraw-history-card__left">
                      <div className="withdraw-history-card__id">
                        #{item.id.slice(0, 6)}
                      </div>

                      <div
                        className={`withdraw-history-status ${getStatusClass(
                          item.status
                        )}`}
                      >
                        {getStatusLabel(item.status)}
                      </div>
                    </div>

                    <div className="withdraw-history-card__right">
                      <div className="withdraw-history-card__amount">
                        {formatMoney(amountUsd)}
                      </div>

                      <div className="withdraw-history-card__tokens">
                        {formatTokens(amountTokens)}{" "}
                        {t("withdrawHistory.tokensWord", {
                          defaultValue: "توكن",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="withdraw-history-card__bottom">
                    <div className="withdraw-history-card__meta">
                      <span className="withdraw-history-card__meta-value">
                        {getMethodLabel(item.method, item.payoutDetails)}
                      </span>
                    </div>

                    <div className="withdraw-history-card__meta-separator">|</div>

                    <div className="withdraw-history-card__meta">
                      <span className="withdraw-history-card__meta-value">
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                  </div>

                  {item?.failureReason ? (
                    <div className="withdraw-history-card__note">
                      {item.failureReason}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}